import { Injectable } from '@angular/core'
import { Observable, combineLatest, debounceTime, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs'
import { EmployerNavigationService } from './navigation.service'
import { GatedSettingsCategory } from './settings-catalog'

const LABEL_HIT = 3
const KEYWORD_HIT = 2
const GROUP_HIT = 1
const DEBOUNCE_MS = 150

/**
 * Client-side keyword search over the authored settings catalog metadata
 * (`label + searchKeywords + group`). This is NOT full page-content search — results
 * are page/section-level (v1). Results are permission/feature filtered via each
 * entry's `isVisible$`, so a user never sees a category they could not open.
 */
@Injectable({ providedIn: 'root' })
export class SettingsSearchService {
  private readonly categories = this.navigationService.settingsCategories

  constructor(private navigationService: EmployerNavigationService) {}

  /**
   * Streams ranked, visible categories for a search term stream. An empty term
   * yields the full visible list (catalog order); otherwise results are ranked
   * label-hit > keyword-hit > group-hit and filtered to matches only.
   */
  search(term$: Observable<string>): Observable<GatedSettingsCategory[]> {
    return term$.pipe(
      debounceTime(DEBOUNCE_MS),
      // After the debounce so the first render carries the list; only typed input waits.
      startWith(''),
      map(term => (term ?? '').trim().toLowerCase()),
      distinctUntilChanged(),
      switchMap(term => this.visibleCategories$().pipe(map(cats => this.rank(cats, term))))
    )
  }

  private visibleCategories$(): Observable<GatedSettingsCategory[]> {
    if (this.categories.length === 0) return of([])
    return combineLatest(this.categories.map(cat => cat.isVisible$.pipe(map(visible => ({ cat, visible }))))).pipe(
      map(entries => entries.filter(entry => entry.visible).map(entry => entry.cat))
    )
  }

  private rank(categories: GatedSettingsCategory[], term: string): GatedSettingsCategory[] {
    if (!term) return categories
    const tokens = term.split(/\s+/).filter(Boolean)
    return categories
      .map(cat => ({ cat, score: this.score(cat, tokens) }))
      .filter(scored => scored.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(scored => scored.cat)
  }

  /** Every token must match somewhere (label/keyword/group) or the category scores 0. */
  private score(cat: GatedSettingsCategory, tokens: string[]): number {
    const label = cat.label.toLowerCase()
    const keywords = cat.searchKeywords.map(keyword => keyword.toLowerCase())
    const group = cat.group.toLowerCase()
    let total = 0
    for (const token of tokens) {
      if (label.includes(token)) total += LABEL_HIT
      else if (keywords.some(keyword => keyword.includes(token))) total += KEYWORD_HIT
      else if (group.includes(token)) total += GROUP_HIT
      else return 0
    }
    return total
  }
}
